import { memo, useMemo, useCallback, useState } from 'react';
import { Star, Youtube, Globe } from 'lucide-react';
import { BaseLanguageSelector, type Language, type QuickAction } from './BaseLanguageSelector';
import { getAllNLLBLanguages, type NLLBLanguage } from '@/utils/languageMapping';
import { getAllYouTubeCodes } from '@/constants/youtubeLanguages';
import { CountryFlag } from '../../CountryFlag';
import { VoiceService } from '@/services';
import type { TTSProvider, Language as LanguageType, VoiceOption } from '@/types';
import { CompactSelect } from '../CompactSelect';
import type { CompactOption } from '../CompactSelect';
import { VoicePreviewButton, type VoicePreviewStatus } from '../../VoicePreviewButton';
import { useClonedVoicesForTTS } from '@/hooks/useClonedVoicesForTTS';
import { useTranslation } from '@/hooks/useTranslation';
import { LocalModelInstallGate } from '../LocalModelInstallGate';

// Top 20 languages ordered by number of speakers worldwide
const TOP_20_LANGUAGE_CODES = [
  'en',
  'zh',
  'hi',
  'es',
  'fr',
  'ar',
  'bn',
  'pt',
  'ru',
  'ja',
  'de',
  'ko',
  'vi',
  'te',
  'mr',
  'ta',
  'tr',
  'it',
  'ur',
  'th',
];

/**
 * Audio language configuration with TTS settings
 */
export interface AudioLanguageConfig {
  language: string;
  provider?: string;
  voice_id?: string;
  /** Foreign accent feature: use voice from different language than text */
  enable_foreign_accent?: boolean;
  /** Voice source language (NLLB code) when accent mode enabled */
  voice_language?: string;
  /** Voice cloning: URL to reference audio for F5-TTS */
  ref_audio_url?: string;
}

export interface AudioLanguageSelectorProps {
  /** Selected language configurations */
  values: AudioLanguageConfig[];
  /** Callback when selection changes */
  onChange: (languages: AudioLanguageConfig[]) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Current TTS provider selected by user - preferred when adding new languages */
  currentProvider?: string;
}

/**
 * AudioLanguageSelector - Language selector with voice configuration for TTS
 *
 * Features:
 * - Language selection with NLLB-200 support
 * - Per-language TTS provider selection
 * - Voice selection with preview
 * - Foreign accent mode
 * - Voice cloning integration (F5-TTS)
 */
export const AudioLanguageSelector = memo(function AudioLanguageSelector({
  values = [],
  onChange,
  disabled = false,
  className = '',
  currentProvider,
}: AudioLanguageSelectorProps) {
  const { t } = useTranslation();
  // Cloned voices + the real state of the local model that has to speak them
  const {
    getClonedVoiceOptions,
    voiceCloningProvider,
    cloningModel,
    isVoiceCloningAvailableForLanguage,
  } = useClonedVoicesForTTS();

  // Load NLLB languages with defensive check (prevents Symbol.iterator error)
  const NLLB_LANGUAGES = useMemo(() => {
    const langs = getAllNLLBLanguages();
    return Array.isArray(langs) ? langs : [];
  }, []);

  // Load YouTube language codes with defensive check
  const YOUTUBE_ALL_LANGUAGE_CODES = useMemo(() => {
    const codes = getAllYouTubeCodes();
    return Array.isArray(codes) ? codes : [];
  }, []);

  // Convert to base Language format, filtered by currentProvider when set.
  // Without a provider filter, the picker would expose languages the provider has
  // no voices for — leading to silent fallbacks where stale provider/voice persist.
  const languages: Language[] = useMemo(() => {
    const all = NLLB_LANGUAGES.map((lang: NLLBLanguage) => ({
      code: lang.code,
      name: lang.name,
      nativeName: lang.nativeName,
      flag: lang.flag,
      region: lang.region,
      nllb_code: lang.nllb_code,
    }));
    if (!currentProvider) return all;
    return all.filter((lang) =>
      VoiceService.isProviderAvailableForLanguage(currentProvider, lang.code)
    );
  }, [NLLB_LANGUAGES, currentProvider]);

  // Defensive check: ensure values is always an array (wrapped in useMemo for stable reference)
  const safeValues = useMemo(() => (Array.isArray(values) ? values : []), [values]);
  const selectedCount = safeValues.length;

  // Track preview status per language so we can render an inline "Voice sample not
  // available for this voice" hint when the backend has no preview.
  const [previewStatus, setPreviewStatus] = useState<Record<string, VoicePreviewStatus>>({});
  const handlePreviewStatusChange = useCallback(
    (langCode: string) => (status: VoicePreviewStatus) => {
      setPreviewStatus((prev) =>
        prev[langCode] === status ? prev : { ...prev, [langCode]: status }
      );
    },
    []
  );

  // Restrict quick-action codes to languages the current provider supports.
  // Without this, "Top 20" with chatterbox_turbo would queue languages it has no voices for.
  const supportedCodes = useMemo(() => new Set(languages.map((l) => l.code)), [languages]);
  const filterByProvider = useCallback(
    (codes: string[]) => codes.filter((c) => supportedCodes.has(c)),
    [supportedCodes]
  );

  // Quick actions for the language dropdown
  const quickActions: QuickAction[] = useMemo(() => {
    const top20 = filterByProvider(TOP_20_LANGUAGE_CODES);
    const youtube = filterByProvider(YOUTUBE_ALL_LANGUAGE_CODES);
    return [
      {
        id: 'top20',
        label: `Top ${top20.length}`,
        description: '70%',
        icon: <Star className="w-3 h-3" />,
        codes: top20,
        className:
          'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/20',
      },
      {
        id: 'youtube',
        label: `YouTube ${youtube.length}`,
        description: 'YouTube',
        icon: <Youtube className="w-4 h-4" />,
        codes: youtube,
        className: 'bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20',
      },
      {
        id: 'all',
        label: `All ${languages.length}`,
        description: 'NLLB-200',
        icon: <Globe className="w-3 h-3" />,
        codes: languages.map((l) => l.code),
        className:
          'bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20',
      },
    ];
  }, [YOUTUBE_ALL_LANGUAGE_CODES, languages, filterByProvider]);

  // Get selected language codes
  const selectedCodes = useMemo(() => safeValues.map((v) => v.language), [safeValues]);

  // Handle language selection from dropdown
  const handleLanguageSelect = useCallback(
    (codes: string[]) => {
      // Find which languages were added or removed
      const currentCodes = safeValues.map((v) => v.language);
      const addedCodes = codes.filter((c) => !currentCodes.includes(c));
      const removedCodes = currentCodes.filter((c) => !codes.includes(c));

      // Build new config array
      let newConfigs = [...safeValues];

      // Remove deselected languages
      newConfigs = newConfigs.filter((config) => !removedCodes.includes(config.language));

      // Add new languages with default voice config
      for (const code of addedCodes) {
        // Prefer the user's currently selected provider if it supports this language
        let provider: string;
        if (currentProvider && VoiceService.isProviderAvailableForLanguage(currentProvider, code)) {
          provider = currentProvider;
        } else {
          provider = VoiceService.getDefaultProviderForLanguage(code);
        }
        const defaultVoice = VoiceService.getDefaultVoice(
          code as LanguageType,
          provider as TTSProvider
        );

        newConfigs.push({
          language: code,
          provider,
          voice_id: defaultVoice,
        });
      }

      // Remove duplicates
      const uniqueConfigs = newConfigs.filter(
        (config, index, self) => index === self.findIndex((c) => c.language === config.language)
      );

      onChange(uniqueConfigs);
    },
    [safeValues, onChange, currentProvider]
  );

  // Update language configuration
  const updateLanguageConfig = useCallback(
    (
      language: string,
      field: 'provider' | 'voice_id' | 'enable_foreign_accent' | 'voice_language',
      value: string | boolean | undefined
    ) => {
      const updated = safeValues.map((config) => {
        if (config.language !== language) return config;

        // Handle accent mode toggle
        if (field === 'enable_foreign_accent') {
          if (value === true) {
            return { ...config, enable_foreign_accent: true };
          } else {
            const defaultProvider = VoiceService.getDefaultProviderForLanguage(config.language);
            const defaultVoice = VoiceService.getDefaultVoice(
              config.language as LanguageType,
              defaultProvider as TTSProvider
            );
            return {
              ...config,
              enable_foreign_accent: false,
              voice_language: undefined,
              provider: defaultProvider,
              voice_id: defaultVoice,
            };
          }
        }

        // Handle voice language selection in accent mode
        // Use the global provider if it supports the accent language, otherwise fallback
        if (field === 'voice_language' && value && typeof value === 'string') {
          let provider: string;
          if (
            currentProvider &&
            VoiceService.isProviderAvailableForLanguage(currentProvider, value)
          ) {
            provider = currentProvider;
          } else {
            provider = VoiceService.getDefaultProviderForLanguage(value);
          }
          const defaultVoice = VoiceService.getDefaultVoice(
            value as LanguageType,
            provider as TTSProvider
          );
          return {
            ...config,
            voice_language: value,
            provider,
            voice_id: defaultVoice,
          };
        }

        // Handle provider change
        if (field === 'provider' && value && typeof value === 'string') {
          const newProvider = value;
          let newVoiceId: string;
          let refAudioUrl: string | undefined;

          if (newProvider === voiceCloningProvider) {
            const clonedVoices = getClonedVoiceOptions(config.language);
            if (clonedVoices.length > 0) {
              const clonedVoice = clonedVoices[0] as VoiceOption & { audioUrl?: string | null };
              newVoiceId = clonedVoice.id;
              refAudioUrl = clonedVoice.audioUrl || undefined;
            } else {
              newVoiceId = VoiceService.getDefaultVoice(
                config.language as LanguageType,
                newProvider as TTSProvider
              );
            }
          } else {
            newVoiceId = VoiceService.getDefaultVoice(
              config.language as LanguageType,
              newProvider as TTSProvider
            );
          }

          return {
            ...config,
            provider: newProvider,
            voice_id: newVoiceId,
            ref_audio_url: refAudioUrl,
          };
        }

        // Handle voice_id change
        if (field === 'voice_id' && value && typeof value === 'string') {
          const newVoiceId = value;
          let refAudioUrl: string | undefined;

          if (newVoiceId.startsWith('clone:') && config.provider === voiceCloningProvider) {
            const clonedVoices = getClonedVoiceOptions(config.language);
            const selectedVoice = clonedVoices.find((v) => v.id === newVoiceId) as
              | (VoiceOption & { audioUrl?: string | null })
              | undefined;
            if (selectedVoice?.audioUrl) {
              refAudioUrl = selectedVoice.audioUrl;
            }
          }

          return {
            ...config,
            voice_id: newVoiceId,
            ref_audio_url: refAudioUrl,
          };
        }

        return { ...config, [field]: value };
      });
      onChange(updated);
    },
    [safeValues, onChange, getClonedVoiceOptions, currentProvider, voiceCloningProvider]
  );

  // Remove a language
  const removeLanguage = useCallback(
    (code: string) => {
      onChange(safeValues.filter((v) => v.language !== code));
    },
    [safeValues, onChange]
  );

  // Get available voices for a language/provider
  const getAvailableVoices = useCallback(
    (languageCode: string, provider: string): VoiceOption[] => {
      return VoiceService.getAvailableVoices(languageCode as LanguageType, provider as TTSProvider);
    },
    []
  );

  // Get voice options for a language/provider
  const getVoiceOptions = useCallback(
    (languageCode: string, provider: string): CompactOption[] => {
      // Defensive: ensure voices is always an array (prevents Symbol.iterator error)
      const rawVoices = getAvailableVoices(languageCode, provider);
      const voices = Array.isArray(rawVoices) ? rawVoices : [];
      let allVoices = voices;

      if (provider === voiceCloningProvider) {
        const rawClonedVoices = getClonedVoiceOptions(languageCode);
        // Defensive: ensure clonedVoices is always an array
        const clonedVoices = Array.isArray(rawClonedVoices) ? rawClonedVoices : [];
        allVoices = [...clonedVoices, ...voices];
      }

      return allVoices.map((voice) => {
        const genderLabel = voice.gender.charAt(0).toUpperCase() + voice.gender.slice(1);
        const gradeLabel = 'grade' in voice && voice.grade ? ` (${voice.grade})` : '';
        const emoticonLabel = 'emoticon' in voice && voice.emoticon ? ` ${voice.emoticon}` : '';

        return {
          value: voice.id,
          label: `${voice.name} (${genderLabel})${gradeLabel}${emoticonLabel}`,
        };
      });
    },
    [getAvailableVoices, getClonedVoiceOptions, voiceCloningProvider]
  );

  /**
   * Whether the preview button can be offered for the selected voice.
   *
   * A cloned voice can only be spoken by the voice-cloning provider, so on a
   * machine where that model isn't installed (or can't run) the preview is a
   * guaranteed failure — the same dead end the install gate above replaces.
   * Non-cloned voices are unaffected, and so is serverless mode, where the
   * install state is never "known" because the GPU worker runs the model.
   */
  const canPreviewVoice = useCallback(
    (voiceId: string | undefined, language: string): boolean => {
      if (!voiceId || voiceId === 'clone_from_reference') return false;
      if (!voiceId.startsWith('clone:')) return true;
      return isVoiceCloningAvailableForLanguage(language);
    },
    [isVoiceCloningAvailableForLanguage]
  );

  // Get selected languages
  const selectedLanguages = useMemo(
    () =>
      NLLB_LANGUAGES.filter((lang: NLLBLanguage) =>
        safeValues.some((v) => v.language === lang.code)
      ),
    [NLLB_LANGUAGES, safeValues]
  );

  // --------------------------------------------------------------------------
  // Voice-cloning install gate
  //
  // Cloned voices are only ever spoken by the voice-cloning provider, and on
  // desktop that provider is a local model that has to be on disk. Without this
  // the user could clone a voice, pick it here, hit Generate and get a
  // mid-generation crash with no explanation and no way forward.
  //
  // Two conditions, both from real state, never from a backend error string:
  //  - the current configuration actually routes through the cloning provider
  //    (globally selected, or selected for at least one language);
  //  - cloningModel.needsInstall, which is only true once /api/v1/components
  //    has ANSWERED and said the model is missing. Before that answer the set
  //    is empty for want of data, not because the model is absent, so the gate
  //    stays hidden and the backend gets to decide (see useLocalModelInstalls).
  // --------------------------------------------------------------------------
  const usesVoiceCloning =
    currentProvider === voiceCloningProvider ||
    safeValues.some((v) => v.provider === voiceCloningProvider);
  const showVoiceCloningGate = usesVoiceCloning && cloningModel.needsInstall;

  return (
    <div className={className}>
      {showVoiceCloningGate && (
        <LocalModelInstallGate
          className="mb-3"
          modelName={cloningModel.name}
          downloadMb={cloningModel.requirements?.downloadMb ?? 0}
          vramGb={cloningModel.requirements?.vramGb ?? 0}
          ramGb={cloningModel.requirements?.ramGb ?? 0}
          diskGb={cloningModel.requirements?.diskGb ?? 0}
          compatible={cloningModel.compatible}
          progress={cloningModel.progress}
          onInstall={cloningModel.install}
          notInstalledDescription={t('voiceClone.localGate.notInstalledDescription', {
            defaultValue:
              'Install it once to speak with your cloned voices on this machine, for free.',
          })}
          incompatibleAction={
            // No second local model can speak a cloned voice, so there is
            // nothing to switch TO here — say what the way out is instead of
            // offering a download that can never run (RULE #5).
            <p className="text-sm text-white/50 text-center px-2">
              {t('voiceClone.localGate.incompatibleHint', {
                defaultValue:
                  'Cloned voices need {{model}}. Choose a different TTS provider to generate audio on this machine.',
                model: cloningModel.name,
              })}
            </p>
          }
        />
      )}

      {/* Quick actions */}
      <div className="button-row-container mb-2">
        <div className="action-bar">
          <div className="button-group">
            {quickActions.map((action) => (
              <div key={action.id} className="flex flex-col">
                <button
                  type="button"
                  onClick={() => handleLanguageSelect(action.codes)}
                  disabled={disabled}
                  className={`action-button ${action.className} disabled:opacity-50`}
                >
                  {action.icon}
                  {action.label}
                </button>
                {action.description && (
                  <span className="fluid-text-xs text-white/40 mt-1 text-center hidden sm:block">
                    {action.description}
                  </span>
                )}
              </div>
            ))}
            {selectedCount > 0 && (
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => onChange([])}
                  disabled={disabled}
                  className="action-button bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          <span className="fluid-text-xs text-white/40 whitespace-nowrap">
            {selectedCount} language{selectedCount !== 1 ? 's' : ''} selected
          </span>
        </div>
      </div>

      {/* Selected languages with configuration */}
      {selectedLanguages.length > 0 && (
        <div className="mb-3 space-y-2">
          {selectedLanguages.map((lang: NLLBLanguage) => {
            const config = safeValues.find((v) => v.language === lang.code);
            if (!config) return null;

            return (
              <div key={lang.code} className="border border-white/10 rounded-lg p-3 bg-black/20">
                {/* Language header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2 flex-1">
                    <CountryFlag code={lang.code} style={{ width: '24px', height: '24px' }} />
                    <span className="font-medium text-sm text-white">{lang.name}</span>
                    <span className="text-xs text-white/50">{lang.nativeName}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLanguage(lang.code)}
                    className="text-red-400 hover:text-red-300 text-lg font-bold px-2"
                    title={t('audioLanguageSelector.removeLanguage')}
                  >
                    x
                  </button>
                </div>

                {/* Foreign accent toggle */}
                <div className="flex items-center space-x-2 mb-3">
                  <input
                    type="checkbox"
                    id={`accent-${lang.code}`}
                    checked={config.enable_foreign_accent || false}
                    onChange={(e) =>
                      updateLanguageConfig(lang.code, 'enable_foreign_accent', e.target.checked)
                    }
                    disabled={disabled}
                    className="h-4 w-4 text-purple-500 border-white/20 rounded bg-black/20 focus:ring-purple-500"
                    data-testid={`accent-toggle-${lang.code}`}
                  />
                  <label
                    htmlFor={`accent-${lang.code}`}
                    className="text-sm text-white/70 cursor-pointer flex items-center"
                  >
                    Foreign Accent
                    <span className="ml-1 text-xs text-white/40">
                      (voice from different language)
                    </span>
                  </label>
                </div>

                {/* Voice configuration */}
                {config.enable_foreign_accent ? (
                  <div className="space-y-3">
                    {/* Text language info */}
                    <div className="p-2 bg-blue-900/20 border border-blue-500/30 rounded text-sm">
                      <span className="text-blue-300 font-medium">
                        {t('audioLanguageSelector.textLanguage')}{' '}
                      </span>
                      <span className="text-blue-400">{lang.name}</span>
                      <span className="text-blue-500 text-xs ml-1">
                        {t('audioLanguageSelector.audioWillSpeak')}
                      </span>
                    </div>

                    {/* Voice language selector */}
                    <div>
                      <label
                        htmlFor={`voice-lang-${lang.code}`}
                        className="block text-sm font-medium text-white/70 mb-1"
                      >
                        {t('audioLanguageSelector.voiceAccentLanguage')}
                      </label>
                      <select
                        id={`voice-lang-${lang.code}`}
                        value={config.voice_language || ''}
                        onChange={(e) =>
                          updateLanguageConfig(lang.code, 'voice_language', e.target.value)
                        }
                        disabled={disabled}
                        className="w-full px-3 py-2 bg-black/20 border border-white/10 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm text-white"
                        data-testid={`voice-lang-${lang.code}`}
                      >
                        <option value="">{t('audioLanguageSelector.selectAccentLanguage')}</option>
                        {NLLB_LANGUAGES.filter((l: NLLBLanguage) => l.code !== lang.code).map(
                          (l: NLLBLanguage) => (
                            <option key={l.code} value={l.code}>
                              {l.name} ({l.nativeName})
                            </option>
                          )
                        )}
                      </select>
                      <p className="text-xs text-white/40 mt-1">
                        {t('audioLanguageSelector.voiceAccentHint')}
                      </p>
                    </div>

                    {/* Voice for accent language (uses global provider) */}
                    {config.voice_language && (
                      <div className="flex items-end fluid-gap-sm">
                        <div className="flex-1">
                          <CompactSelect
                            label={t('audioLanguageSelector.voice')}
                            options={getVoiceOptions(config.voice_language, config.provider || '')}
                            value={config.voice_id || ''}
                            onChange={(value) => updateLanguageConfig(lang.code, 'voice_id', value)}
                            disabled={disabled}
                            testId={`voice-${lang.code}`}
                          />
                        </div>
                        {config.voice_id &&
                          config.provider &&
                          canPreviewVoice(config.voice_id, config.voice_language || lang.code) && (
                            <VoicePreviewButton
                              provider={config.provider}
                              voiceId={config.voice_id}
                              language={config.voice_language || lang.code}
                              disabled={disabled}
                              size="md"
                              className="mb-0.5"
                              onStatusChange={handlePreviewStatusChange(lang.code)}
                            />
                          )}
                      </div>
                    )}
                    {previewStatus[lang.code] === 'unavailable' && (
                      <p className="text-xs text-white/50 mt-1">
                        {t('audioLanguageSelector.voiceSampleUnavailable')}
                      </p>
                    )}
                  </div>
                ) : (
                  /* Normal mode: Voice from same language (provider from global selector) */
                  <div>
                    <div className="flex items-end fluid-gap-sm">
                      <div className="flex-1">
                        <CompactSelect
                          label={t('audioLanguageSelector.voice')}
                          options={getVoiceOptions(lang.code, config.provider || '')}
                          value={config.voice_id || ''}
                          onChange={(value) => updateLanguageConfig(lang.code, 'voice_id', value)}
                          disabled={disabled}
                          testId={`voice-${lang.code}`}
                        />
                      </div>
                      {config.voice_id &&
                        config.provider &&
                        canPreviewVoice(config.voice_id, lang.code) && (
                          <VoicePreviewButton
                            provider={config.provider}
                            voiceId={config.voice_id}
                            language={lang.code}
                            disabled={disabled}
                            size="md"
                            className="mb-0.5"
                            onStatusChange={handlePreviewStatusChange(lang.code)}
                          />
                        )}
                    </div>
                    {previewStatus[lang.code] === 'unavailable' && (
                      <p className="text-xs text-white/50 mt-1">
                        {t('audioLanguageSelector.voiceSampleUnavailable')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add language dropdown */}
      <BaseLanguageSelector
        languages={languages}
        values={selectedCodes}
        onChange={handleLanguageSelect}
        disabled={disabled}
        showQuickActions={false}
        placeholder={selectedCount === 0 ? 'Add language' : 'Add more languages'}
        searchPlaceholder={`Search ${languages.length} languages...`}
        footerDescription={`NLLB-200 - ${languages.length} languages supported`}
        topLanguageCodes={TOP_20_LANGUAGE_CODES}
        topLanguagesLabel="Top 20 Most Spoken"
        testId="multilingual-audio-selector"
        renderLanguageItem={(lang, isSelected) => (
          <>
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}}
                className="h-4 w-4 text-primary-500 border-white/20 rounded bg-black/20 focus:ring-primary-500"
                onClick={(e) => e.stopPropagation()}
                data-testid={`audio-lang-${lang.code}`}
              />
              <CountryFlag code={lang.code} style={{ width: '24px', height: '24px' }} />
              <div className="text-left">
                <div className="text-sm font-medium text-white flex items-center">
                  {lang.name}
                  {TOP_20_LANGUAGE_CODES.includes(lang.code) && (
                    <span className="ml-1 text-xs bg-yellow-500/20 text-yellow-400 px-1 rounded">
                      #{TOP_20_LANGUAGE_CODES.indexOf(lang.code) + 1}
                    </span>
                  )}
                </div>
                <div className="text-xs text-white/50">{lang.nativeName}</div>
              </div>
            </div>
            <span className="text-xs text-white/30 uppercase">{lang.code}</span>
          </>
        )}
      />

      {/* Summary */}
      {selectedCount > 0 && (
        <p className="text-xs text-white/40 mt-2">
          Audio will be generated in <strong>{selectedCount}</strong> language
          {selectedCount !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
});

export default AudioLanguageSelector;
